import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { User, LogOut, Sparkles, Menu, X, UserCircle2, Settings, Library as LibraryIcon, Shield, Trophy, Info, MessageSquare, Bot, Wrench, Users, ImageIcon, Globe, FileText, Wand2, Compass, Rocket, Video, Lock } from "lucide-react";
import MentionBell from "./MentionBell";
import { cn } from "@/lib/utils";
import AuthModal from "./AuthModal";
import TrialBadge from "./TrialBadge";
import ThemeToggle from "./ThemeToggle";
import SampleCountdownChip from "./sample/SampleCountdownChip";
import { useAuth } from "@/hooks/useAuth";
import { useMyProfile } from "@/hooks/useMyProfile";
import { useUserRole } from "@/hooks/useUserRole";
import { useXP, tierLabel } from "@/hooks/useXP";
import { useMyWeeklyRank } from "@/hooks/useMyWeeklyRank";
import { useNewFeedbackNotifier } from "@/hooks/useNewFeedbackNotifier";
import { useSquadsUnlocked, SQUADS_UNLOCK_AT } from "@/hooks/useBeginnerMode";
import { flagFor } from "@/lib/countries";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { subscribeChatMode, type ChatModeId } from "@/lib/chatModeBus";

// Returning visitors get "Sign in" as the primary CTA; brand new visitors
// see "Join free" as primary. We mark someone as "returning" the first time
// they successfully authenticate from this device — see AuthModal.
const RETURNING_KEY = "hl_returning";
const isReturningVisitor = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(RETURNING_KEY) === "1";
  } catch {
    return false;
  }
};

// Radically trimmed top nav: the desktop header now shows ONLY the
// logo (which IS the Chat home link) plus the account dropdown. The
// secondary destinations below live inside the dropdown under an
// "Explore" group, and on mobile they're reachable from a 3-tab
// bottom bar (Chat / Learn / Menu). This keeps the chrome out of the
// way so the chat surface itself does the talking.
const NAV_LINKS: { to: string; label: string; icon: typeof MessageSquare }[] = [];

// "Explore" group — the four canonical destinations, surfaced inside
// the account dropdown on desktop and the slide-down panel on mobile.
const EXPLORE_LINKS: { to: string; label: string; icon: typeof MessageSquare }[] = [
  { to: "/roadmap", label: "Learn", icon: Compass },
  { to: "/agents", label: "Agents", icon: Bot },
  { to: "/directory", label: "Directory", icon: Wrench },
  { to: "/library", label: "Library", icon: LibraryIcon },
];

// Secondary destinations surfaced only when the user opens a menu
// (account dropdown on desktop, hamburger panel on mobile). Accelerator
// is intentionally NOT in the always-visible header so the home/desktop
// view feels like a single full-bleed surface — users pull it down from
// the menu when they want it. Squad Rooms (live co-work) lives here too.
const PULL_DOWN_LINKS: { to: string; label: string; icon: typeof MessageSquare }[] = [
  { to: "/accelerator", label: "Accelerator", icon: Rocket },
  { to: "/squad-rooms", label: "Squad Rooms", icon: Video },
];

// Quick actions shown as one labeled section inside the account
// dropdown (and mirrored in the signed-in mobile menu). Replaces what
// used to be the "Chat actions" submenu — now flat, three rows, no
// submenu hover so the menu reads as one scannable list.
const QUICK_ACTIONS: { to: string; label: string; icon: typeof MessageSquare; lockedUntilSkill?: number }[] = [
  { to: "/squads", label: "Squads", icon: Users, lockedUntilSkill: SQUADS_UNLOCK_AT },
  { to: "/quests", label: "Quests", icon: Sparkles },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy },
];

// Per-mode metadata for the in-Navbar "you are here" badge. The Chat
// shell publishes its current mode via chatModeBus when the user is on
// "/", so we can show e.g. "Image" or "Research" instead of a generic
// "Chat" pill. When the user navigates to a real route (Library,
// Agents, Directory, etc.) we fall back to a route-derived label.
const CHAT_MODE_META: Record<ChatModeId, { label: string; icon: typeof MessageSquare }> = {
  chat: { label: "Chat", icon: MessageSquare },
  image: { label: "Image", icon: ImageIcon },
  research: { label: "Research", icon: Globe },
  analyze: { label: "Analyze", icon: FileText },
  builder: { label: "Builder", icon: Wand2 },
  library: { label: "Library", icon: LibraryIcon },
};

// Route-prefix → badge label fallback used when the user is NOT on the
// home/chat route. Order matters: longer prefixes first so /admin/site
// wins over a hypothetical /admin shorter match.
const ROUTE_BADGES: { match: (p: string) => boolean; label: string; icon: typeof MessageSquare }[] = [
  { match: (p) => p.startsWith("/admin"), label: "Admin", icon: Shield },
  { match: (p) => p.startsWith("/agents"), label: "Agents", icon: Bot },
  { match: (p) => p.startsWith("/directory"), label: "Directory", icon: Wrench },
  { match: (p) => p.startsWith("/squads"), label: "Squads", icon: Users },
  { match: (p) => p.startsWith("/library"), label: "Library", icon: LibraryIcon },
  { match: (p) => p.startsWith("/accelerator"), label: "Accelerator", icon: Rocket },
  { match: (p) => p.startsWith("/squad-rooms"), label: "Squad Rooms", icon: Video },
  { match: (p) => p.startsWith("/roadmap"), label: "Roadmap", icon: Compass },
  { match: (p) => p.startsWith("/settings"), label: "Settings", icon: Settings },
  { match: (p) => p.startsWith("/account"), label: "Account", icon: Sparkles },
  { match: (p) => p.startsWith("/profile"), label: "Profile", icon: UserCircle2 },
  { match: (p) => p.startsWith("/leaderboard"), label: "Leaderboard", icon: Trophy },
  { match: (p) => p.startsWith("/about"), label: "About", icon: Info },
];

// Tiny hook: subscribes to the chat-mode bus + reads the current route
// to decide which badge to render. Returns null when there's nothing
// meaningful to show (e.g. landing on "/" before the chat shell has
// mounted, or on an unknown path).
const useCurrentBadge = () => {
  const location = useLocation();
  const [chatMode, setChatModeState] = useState<ChatModeId | null>(null);
  useEffect(() => {
    return subscribeChatMode((m) => setChatModeState(m));
  }, []);
  // On the home route prefer the live chat mode; everywhere else use
  // the route table.
  if (location.pathname === "/") {
    if (!chatMode) return null;
    const meta = CHAT_MODE_META[chatMode];
    if (!meta) return null;
    return { label: meta.label, Icon: meta.icon };
  }
  const hit = ROUTE_BADGES.find((r) => r.match(location.pathname));
  if (!hit) return null;
  return { label: hit.label, Icon: hit.icon };
};

const Navbar = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { profile } = useMyProfile();
  const { isAdmin } = useUserRole();
  const { xp } = useXP();
  const { data: weeklyRank } = useMyWeeklyRank();
  // Live admin notifications for new feedback/bug reports.
  // The hook self-gates on isAdmin, so non-admins pay no cost.
  const { unreadCount: newFeedbackCount } = useNewFeedbackNotifier();
  const squadsUnlocked = useSquadsUnlocked();
  // Snapshot once per render — localStorage is sync so no flicker.
  const returning = isReturningVisitor();
  // "You are here" badge — chat mode when on /, route label otherwise.
  const currentBadge = useCurrentBadge();

  const openAuth = (mode: "login" | "signup") => {
    setAuthMode(mode);
    setShowAuth(true);
  };

  const closeMobile = () => setMobileOpen(false);

  // Display name shown in the dropdown trigger; falls back through
  // profile → auth metadata → email local-part so signed-in users always
  // see *something*, even before /profiles loads.
  const displayName =
    profile?.displayName ||
    profile?.username ||
    (user?.user_metadata?.full_name as string | undefined) ||
    user?.email?.split("@")[0] ||
    "Account";

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-4 h-14 flex items-center justify-between">
          <Link
            to="/"
            data-tour="nav-workspace"
            className="flex items-center hover:opacity-80 transition-opacity"
            aria-label="HackerLabs.ai home"
            onClick={closeMobile}
          >
            <span className="text-xl font-mono font-extrabold">
              <span className="text-foreground">Hacker</span>
              <span className="text-primary">Labs</span>
              <span className="text-muted-foreground">.ai</span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-4">
            {NAV_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-1.5 text-xs font-mono transition-colors hover:text-primary",
                      isActive ? "text-primary" : "text-muted-foreground",
                    )
                  }
                >
                  {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
                  {link.label}
                </NavLink>
              );
            })}
            {/* "You are here" badge — reflects the live chat mode when
                the user is on / (Image / Research / Analyze / Builder /
                Library), or the current top-level route everywhere
                else. Hidden when there's nothing meaningful to show, or
                when a primary nav link already labels the current page
                (avoids e.g. two "Library" chips in the header). */}
            {currentBadge &&
              !NAV_LINKS.some(
                (l) => l.label.toLowerCase() === currentBadge.label.toLowerCase(),
              ) && (
              <span
                className="hidden sm:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full border border-primary/30 bg-primary/10 text-primary"
                title={`You are here: ${currentBadge.label}`}
                aria-label={`Current section: ${currentBadge.label}`}
              >
                <currentBadge.Icon className="w-3 h-3" />
                {currentBadge.label}
              </span>
            )}
            <SampleCountdownChip />
            <ThemeToggle />
            {user && <MentionBell />}
            {user ? (
              // Single dropdown reclaims header real estate: name → menu.
              // TrialBadge moves inside so it's never lost; sign-out lives
              // at the bottom; "View my profile" deep-links to the public
              // /profile/:username page when we have a username.
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-tour="nav-account"
                  className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-foreground transition-colors max-w-[160px] focus:outline-none focus:ring-2 focus:ring-ring rounded-md px-1.5 py-1"
                  aria-label="Open account menu"
                >
                  {profile?.countryCode ? (
                    <span
                      className="text-sm leading-none shrink-0"
                      title={profile.countryCode}
                      aria-hidden
                    >
                      {flagFor(profile.countryCode)}
                    </span>
                  ) : (
                    <UserCircle2 className="w-3.5 h-3.5 shrink-0" />
                  )}
                  <span className="truncate">{displayName}</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 font-mono">
                  {/* Header block: name + tagline directly under it. The
                      tagline used to live in DropdownMenuLabel which made
                      it feel disconnected from the user. Now it's the
                      subtitle of the displayName so the relationship is
                      obvious and the stack hugs the dropdown width. */}
                  <div className="px-2 pt-2 pb-1.5">
                    <p className="text-xs font-mono font-semibold text-foreground truncate">
                      {displayName}
                    </p>
                    <p className="text-[10px] font-mono text-muted-foreground/70 leading-snug mt-0.5">
                      Level up by producing real outcomes.
                    </p>
                  </div>
                  {/* Full-width divider between header and gamification
                      score so the score reads as its own surface but
                      stays visually attached to the dropdown frame. */}
                  <DropdownMenuSeparator className="my-0" />
                  {/* Gamification score: simplified single row that
                      spans the full dropdown width (no inset padding
                      box around it). Level + tier on the left, weekly
                      rank on the right. Tap to open the leaderboard. */}
                  {xp && (
                    <Link
                      to="/leaderboard"
                      className="flex items-center justify-between gap-2 px-2.5 py-2 bg-primary/5 hover:bg-primary/10 transition-colors"
                      aria-label="Open global leaderboard"
                    >
                      <span className="flex items-center gap-1.5 text-[11px] font-mono">
                        <Trophy className="w-3.5 h-3.5 text-primary shrink-0" />
                        <span className="font-bold text-primary">Lv {xp.level}</span>
                        <span className="text-primary/40">·</span>
                        <span className="uppercase tracking-wider text-primary">
                          {tierLabel(xp.tier)}
                        </span>
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground truncate">
                        {weeklyRank ? `#${weeklyRank.rank} this wk` : "Unranked"}
                      </span>
                    </Link>
                  )}
                  <DropdownMenuSeparator className="my-0" />
                  <div className="px-2 py-2">
                    <TrialBadge />
                  </div>
                  <DropdownMenuSeparator />
                  {/* Quick actions: flat group for the three power
                      destinations users still want one-tap access to.
                      Roadmap and Recent stay behind /library + /recent
                      slash commands inside the chat shell. */}
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-mono px-2 pt-1.5 pb-1">
                    Explore
                  </DropdownMenuLabel>
                  {EXPLORE_LINKS.map((link) => {
                    const Icon = link.icon;
                    return (
                      <DropdownMenuItem key={link.to} asChild>
                        <Link to={link.to} className="cursor-pointer text-xs">
                          <Icon className="w-3.5 h-3.5 mr-2" />
                          {link.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-mono px-2 pt-1.5 pb-1">
                    Quick actions
                  </DropdownMenuLabel>
                  {PULL_DOWN_LINKS.map((link) => {
                    const Icon = link.icon;
                    return (
                      <DropdownMenuItem key={link.to} asChild>
                        <Link to={link.to} className="cursor-pointer text-xs">
                          <Icon className="w-3.5 h-3.5 mr-2" />
                          {link.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  {QUICK_ACTIONS.map((link) => {
                    const Icon = link.icon;
                    const isLocked = link.lockedUntilSkill != null && !squadsUnlocked;
                    if (isLocked) {
                      return (
                        <DropdownMenuItem
                          key={link.to}
                          disabled
                          className="text-xs opacity-60 cursor-not-allowed"
                        >
                          <Icon className="w-3.5 h-3.5 mr-2" />
                          <span className="flex-1">{link.label}</span>
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
                            <Lock className="w-3 h-3" />#{link.lockedUntilSkill}
                          </span>
                        </DropdownMenuItem>
                      );
                    }
                    return (
                      <DropdownMenuItem key={link.to} asChild>
                        <Link to={link.to} className="cursor-pointer text-xs">
                          <Icon className="w-3.5 h-3.5 mr-2" />
                          {link.label}
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                  <DropdownMenuSeparator />
                  {profile?.username ? (
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${profile.username}`} className="cursor-pointer text-xs">
                        <UserCircle2 className="w-3.5 h-3.5 mr-2" />
                        View my profile
                      </Link>
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer text-xs">
                      <Settings className="w-3.5 h-3.5 mr-2" />
                      Account & settings
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin/feedback" className="cursor-pointer text-xs flex items-center justify-between gap-2">
                          <span className="flex items-center">
                            <Shield className="w-3.5 h-3.5 mr-2" />
                            Admin console
                          </span>
                          {newFeedbackCount > 0 && (
                            <span className="ml-auto inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-bold">
                              {newFeedbackCount > 99 ? "99+" : newFeedbackCount}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={signOut}
                    className="cursor-pointer text-xs text-muted-foreground"
                  >
                    <LogOut className="w-3.5 h-3.5 mr-2" />
                    Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Returning visitors: Sign in is primary, Join is ghost.
              // First-time visitors: Join is primary, Sign in is ghost.
              <div className="flex items-center gap-2">
                {/* Compact Explore menu so signed-out visitors can still
                    reach Learn / Agents / Directory / Library without
                    cluttering the always-visible header. */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className="text-xs font-mono px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                    aria-label="Open explore menu"
                  >
                    Explore
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44 font-mono">
                    {EXPLORE_LINKS.map((link) => {
                      const Icon = link.icon;
                      return (
                        <DropdownMenuItem key={link.to} asChild>
                          <Link to={link.to} className="cursor-pointer text-xs">
                            <Icon className="w-3.5 h-3.5 mr-2" />
                            {link.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/about" className="cursor-pointer text-xs">
                        <Info className="w-3.5 h-3.5 mr-2" />
                        About
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {returning ? (
                  <>
                    <button
                      onClick={() => openAuth("signup")}
                      className="text-xs font-mono px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      Join
                    </button>
                    <button
                      onClick={() => openAuth("login")}
                      className="flex items-center gap-1.5 text-xs font-mono font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                    >
                      <User className="w-3.5 h-3.5" /> Sign in
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => openAuth("login")}
                      className="text-xs font-mono px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      Sign in
                    </button>
                    <button
                      onClick={() => openAuth("signup")}
                      className="flex items-center gap-1.5 text-xs font-mono font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Start free
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Mobile: just the theme toggle in the header. Primary
              navigation lives in the persistent bottom tab bar below
              (Chat / Learn / Menu) so the thumb has a fixed target. */}
          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle />
            {user && <MentionBell />}
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div
            id="mobile-nav"
            className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl"
          >
            <div className="container mx-auto px-4 py-3 flex flex-col gap-1">
              {NAV_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-mono transition-colors hover:text-primary hover:bg-secondary",
                        isActive
                          ? "text-primary bg-secondary/60"
                          : "text-muted-foreground",
                      )
                    }
                  >
                    {Icon ? <Icon className="w-4 h-4" /> : null}
                    {link.label}
                  </NavLink>
                );
              })}
              {PULL_DOWN_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end
                    onClick={closeMobile}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-mono transition-colors hover:text-primary hover:bg-secondary",
                        isActive
                          ? "text-primary bg-secondary/60"
                          : "text-muted-foreground",
                      )
                    }
                  >
                    {Icon ? <Icon className="w-4 h-4" /> : null}
                    {link.label}
                  </NavLink>
                );
              })}

              {/* About link in mobile nav */}
              <div className="border-t border-border mt-2 pt-2">
                <Link
                  to="/about"
                  onClick={closeMobile}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                >
                  <Info className="w-4 h-4" /> About
                </Link>
              </div>

              <div className="border-t border-border mt-2 pt-3">
                {user ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-mono text-muted-foreground px-3 py-1.5 truncate">
                      {displayName}
                    </span>
                    {xp && (
                      <Link
                        to="/leaderboard"
                        onClick={closeMobile}
                        className="mx-3 mb-2 flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/10 hover:bg-primary/20 px-2.5 py-2 transition-colors"
                        aria-label="Open global leaderboard"
                      >
                        <span className="flex items-center gap-1.5 text-xs font-mono">
                          <Trophy className="w-3.5 h-3.5 text-primary" />
                          <span className="font-bold text-primary">Lv {xp.level}</span>
                          <span className="text-primary/60">·</span>
                          <span className="uppercase tracking-wider text-primary">
                            {tierLabel(xp.tier)}
                          </span>
                        </span>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {weeklyRank ? `#${weeklyRank.rank} this week` : "Unranked"}
                        </span>
                      </Link>
                    )}
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70 font-mono px-3 pt-2 pb-1">
                      Quick actions
                    </span>
                    {QUICK_ACTIONS.map((link) => {
                      const Icon = link.icon;
                      const isLocked = link.lockedUntilSkill != null && !squadsUnlocked;
                      if (isLocked) {
                        return (
                          <div
                            key={link.to}
                            className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground/60 cursor-not-allowed"
                          >
                            <Icon className="w-4 h-4" />
                            <span className="flex-1">{link.label}</span>
                            <span className="inline-flex items-center gap-1 text-[10px]">
                              <Lock className="w-3 h-3" />#{link.lockedUntilSkill}
                            </span>
                          </div>
                        );
                      }
                      return (
                        <Link
                          key={link.to}
                          to={link.to}
                          onClick={closeMobile}
                          className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <Icon className="w-4 h-4" /> {link.label}
                        </Link>
                      );
                    })}
                    {profile?.username ? (
                      <Link
                        to={`/profile/${profile.username}`}
                        onClick={closeMobile}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                      >
                        <UserCircle2 className="w-4 h-4" /> View my profile
                      </Link>
                    ) : null}
                    <Link
                      to="/settings"
                      onClick={closeMobile}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <Settings className="w-4 h-4" /> Account & settings
                    </Link>
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin/feedback"
                          onClick={closeMobile}
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <Shield className="w-4 h-4" /> Admin console
                          </span>
                          {newFeedbackCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-bold">
                              {newFeedbackCount > 99 ? "99+" : newFeedbackCount}
                            </span>
                          )}
                        </Link>
                      </>
                    )}
                    <button
                      onClick={() => {
                        signOut();
                        closeMobile();
                      }}
                      className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors text-left"
                    >
                      <LogOut className="w-4 h-4" /> Sign out
                    </button>
                  </div>
                ) : returning ? (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        openAuth("login");
                        closeMobile();
                      }}
                      className="w-full flex items-center justify-center gap-1.5 text-xs font-mono font-semibold px-3 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <User className="w-3.5 h-3.5" /> Sign in
                    </button>
                    <button
                      onClick={() => {
                        openAuth("signup");
                        closeMobile();
                      }}
                      className="w-full flex items-center justify-center gap-1.5 text-xs font-mono px-3 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Join
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        openAuth("signup");
                        closeMobile();
                      }}
                      className="w-full flex items-center justify-center gap-1.5 text-xs font-mono font-semibold px-3 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Join free
                    </button>
                    <button
                      onClick={() => {
                        openAuth("login");
                        closeMobile();
                      }}
                      className="w-full flex items-center justify-center gap-1.5 text-xs font-mono px-3 py-2.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    >
                      <User className="w-3.5 h-3.5" /> Sign in
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      {/* Mobile bottom tab bar — three thumb-friendly targets that
          replace the old top-right hamburger. "Menu" toggles the same
          slide-down panel rendered inside <nav> above, so all the
          existing account/quick-action items remain reachable in one
          tap. The spacer pad is handled per-page via safe-area aware
          body padding (added in index.css) so this bar never covers
          page content. */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
        role="navigation"
        aria-label="Primary"
      >
        <div className="flex items-stretch h-14">
          <NavLink
            to="/"
            end
            onClick={closeMobile}
            className={({ isActive }) =>
              cn(
                "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-mono transition-colors",
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            <MessageSquare className="w-5 h-5" />
            Chat
          </NavLink>
          <NavLink
            to="/roadmap"
            data-tour="nav-mastery"
            onClick={closeMobile}
            className={({ isActive }) =>
              cn(
                "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-mono transition-colors",
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )
            }
          >
            <Compass className="w-5 h-5" />
            Learn
          </NavLink>
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className={cn(
              "flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-mono transition-colors",
              mobileOpen ? "text-primary" : "text-muted-foreground hover:text-foreground",
            )}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            Menu
          </button>
        </div>
      </div>
      <AuthModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        defaultMode={authMode}
      />
    </>
  );
};

export default Navbar;
